import { Tenant } from "@/payload-types";
import { LocaleCode, LocaleSlug, SLUG_TO_LOCALE, LOCALE_TO_SLUG } from "@/lib/labels/types";

export type LocaleInfo = {
    locale: LocaleCode;
    slug: LocaleSlug;
    pathWithoutLocale: string;
    isMultiLang: boolean;
};

/**
 * Quick locale detection from URL without tenant data
 * Returns the locale code if found in URL, otherwise "en"
 */
export function detectLocaleFromSlug(slugParts: string[]): LocaleCode {
    const firstSegment = slugParts[0] as LocaleSlug | undefined;

    if (firstSegment && firstSegment in SLUG_TO_LOCALE) {
        return SLUG_TO_LOCALE[firstSegment];
    }

    return "en"; // default
}

/**
 * Extract locale from URL path based on tenant configuration
 *
 * Single-language tenant:
 *   /standings → locale from config, path=/standings
 *
 * Multi-language tenant:
 *   /de/standings → locale=de, path=/standings
 *   /fr/standings → locale=fr, path=/standings
 */
export function extractLocale(slugParts: string[], tenant: Tenant | undefined): LocaleInfo {
    const defaultLocale = (tenant?.localization?.defaultLocale || "en") as LocaleCode;
    const enabledLocales = getEnabledLocales(tenant);
    const isMultiLang = enabledLocales.length > 1;

    // Single language tenant - no URL prefix
    if (!isMultiLang) {
        return {
            locale: defaultLocale,
            slug: LOCALE_TO_SLUG[defaultLocale],
            pathWithoutLocale: "/" + slugParts.join("/"),
            isMultiLang: false,
        };
    }

    // Multi-language tenant - check for locale prefix
    const firstSegment = slugParts[0] as LocaleSlug | undefined;

    if (firstSegment && firstSegment in SLUG_TO_LOCALE) {
        const localeCode = SLUG_TO_LOCALE[firstSegment];

        // Verify this locale is enabled for this tenant
        if (enabledLocales.includes(localeCode)) {
            return {
                locale: localeCode,
                slug: firstSegment,
                pathWithoutLocale: "/" + slugParts.slice(1).join("/") || "/",
                isMultiLang: true,
            };
        }
    }

    // No valid locale prefix - use default
    return {
        locale: defaultLocale,
        slug: LOCALE_TO_SLUG[defaultLocale],
        pathWithoutLocale: "/" + slugParts.join("/"),
        isMultiLang: true,
    };
}

/**
 * Get the effective list of enabled locales for a tenant.
 * Always includes the default locale even if not explicitly in enabledLocales.
 */
export function getEnabledLocales(tenant: Tenant | undefined): LocaleCode[] {
    const configuredLocales = (tenant?.localization?.enabledLocales || ["en"]) as LocaleCode[];
    const defaultLocale = (tenant?.localization?.defaultLocale || "en") as LocaleCode;
    if (configuredLocales.includes(defaultLocale)) return configuredLocales;
    return [defaultLocale, ...configuredLocales];
}

/**
 * Build a localized URL path
 */
export function buildLocalePath(path: string, locale: LocaleCode, isMultiLang: boolean): string {
    if (!isMultiLang) return path;
    const slug = LOCALE_TO_SLUG[locale];
    return `/${slug}${path === "/" ? "" : path}`;
}
